import { Box, Button, Typography, useTheme } from '@mui/material';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import Breadcrumbs from 'components/breadcrumbs';
import Error from 'components/error';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';

import { useAccountState } from 'contexts/account/account-context';
import BluViuNutritionTabsHeader from 'views/bluviu-nutrition/components/bluviu-nutrition-tabs-header';
import ScenarioSetupTab from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab';
import ScenarioSubTabs from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-sub-tabs';

import InfoBox from 'components/info-box';
import KebabMenu from 'components/kebab-menu';
import titleCase from 'utils/title-case';
import { BluViuNutritionProvider } from 'views/bluviu-nutrition/context/bluviu-nutrition-context';
import ScenarioCloneDialog from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-clone-dialog';
import ScenarioDeleteDialog from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-delete-dialog';
import ScenarioResultTab from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab';
import {
  ScenarioProvider,
  useR2ScenarioState,
} from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { ScenarioResultProvider } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';
import { ScenarioSubTab as Tabs } from '../types/scenario-sub-tab.enum';
import ScenarioAppliedTab from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-applied-tab/scenario-applied-tab';
import { CircleAlert, X } from 'lucide-react';
import IconButton from 'components/icon-button';
import dayjs from 'dayjs';
import ScenarioInfoGrid from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-info-grid';

type Props = {};

const ScenarioOverview: React.FC<Props> = () => {
  const theme = useTheme();
  const intl = useIntl();
  const { globalSiteId, scenarioId, subTab } = useParams<R2ScenarioParams>();
  const [hideBanner, setHideBanner] = React.useState(false);

  const {
    account: { company },
  } = useAccountState();

  const {
    r2ScenarioQuery: { data: scenario, error: scenarioError, isError: isScenarioError, isLoading: isScenarioLoading },
    r2ScenarioStatusQuery: { data: scenarioStatus },
  } = useR2ScenarioState();

  if (isScenarioLoading) return <LoadingSpinner />;
  if (isScenarioError) return <Error error={scenarioError} />;

  const site = company?.addressAccess.find((s) => s.id === globalSiteId); // should use scenario's globalSiteId but since data is mocked we use this temporarily

  return (
    !!scenario && (
      <>
        <BluViuNutritionTabsHeader
          intro={
            <Typography color="white">
              <FormattedMessage id={`general.confidential`} />
            </Typography>
          }
          utils={
            <Breadcrumbs
              crumbs={
                !!globalSiteId
                  ? [
                      {
                        text: site?.name || '',
                        url: `${globalSiteId}`,
                      },
                      {
                        text: scenario.name,
                        url: `${globalSiteId}/${scenarioId}`,
                      },
                    ]
                  : undefined
              }
            />
          }
        />
        <Box display="flex" flexDirection="column" gap={6}>
          {scenarioStatus?.scenarioIsRunning && (
            <InfoBox variant="light">
              <Box>
                <Typography fontWeight={700} color="inherit">
                  Scenario in progress
                </Typography>
                <Typography variant="body2" color="inherit">
                  You are free to leave this page while the scenario is running, which may take a while depending on the complexity of
                  your site data.
                </Typography>
              </Box>
            </InfoBox>
          )}
          <ScenarioInfoGrid
            items={[
              { label: intl.formatMessage({ id: 'general.site' }), value: site?.name || 'N/A' },
              { label: 'Scenario name', value: scenario.name, noWrap: true },
              {
                label: 'Scenario start',
                value: scenario.scenarioStart ? dayjs(scenario.scenarioStart).utc().format('DD-MM-YYYY') : 'N/A',
              },

              { label: 'State', value: scenario.appliedAs ? 'Applied' : titleCase(scenario.state) },
            ]}
          />
          {scenario.appliedAs && !hideBanner && (
            <Box
              borderRadius={2}
              sx={{ background: theme.palette.blue.o25 }}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'stretch'}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minHeight: '90px' }}>
                <Box sx={{ flexShrink: 0, width: '14px' }} />
                <Box>
                  <CircleAlert size={24} color={theme.palette.darkBlue.main} />
                </Box>
                <Box>
                  <Typography fontSize={14} fontWeight={600} color={theme.palette.darkBlue.main}>
                    Clone to make adjustments
                  </Typography>
                  <Typography fontSize={14} color={theme.palette.darkBlue.main}>
                    This scenario has been applied and cannot be updated. Clone it to reuse your setup and adjust for a new run.
                  </Typography>
                </Box>
                <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} alignItems={'center'} flex={1} gap={4}>
                  <ScenarioCloneDialog
                    scenario={scenario}
                    button={
                      <Button variant="outlined" color="secondary">
                        <Typography fontSize={14} color={theme.palette.darkBlue.main}>
                          Clone
                        </Typography>
                      </Button>
                    }
                  />
                  <IconButton variant="text" onClick={() => setHideBanner(true)} sx={{ background: theme.palette.blue.o25 }}>
                    <X size={24} color={theme.palette.darkBlue.main} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}

          <Box display={'flex'} justifyContent={'space-between'}>
            <ScenarioSubTabs />
            <KebabMenu>
              <Box display="flex" flexDirection="column" alignItems="flex-start">
                <ScenarioCloneDialog scenario={scenario} />
                <ScenarioDeleteDialog scenario={scenario} />
              </Box>
            </KebabMenu>
          </Box>
        </Box>
        <ScenarioResultProvider>
          <Paper transparent sx={{ display: 'flex', borderTopLeftRadius: { xs: 0, md: '1rem' }, flexDirection: 'column', gap: 4 }}>
            {subTab === Tabs.SETUP && (
              <Box display={'flex'} flexDirection={'column'} gap={4}>
                <ScenarioSetupTab />
              </Box>
            )}
            {subTab === Tabs.RESULTS && (
              <Box display={'flex'} flexDirection={'column'} gap={4}>
                <ScenarioResultTab />
              </Box>
            )}
            {subTab === Tabs.APPLIED && (
              <Box display={'flex'} flexDirection={'column'} gap={4}>
                <ScenarioAppliedTab />
              </Box>
            )}
          </Paper>
        </ScenarioResultProvider>
      </>
    )
  );
};

/**
 * We may need to swap this for a new scenario context specific to Scenario v2. We don't want to * mess with this context because the Calibrations / Scenarios tabs rely on it
 * */
const ScenarioContainer: React.FC<{}> = () => {
  return (
    // ScenarioProvider wrapped in BluViuNutritionProvider to have access to sites and company info for clone dialog, it will be refactored later.
    <BluViuNutritionProvider>
      <ScenarioProvider>
        <ScenarioOverview />
      </ScenarioProvider>
    </BluViuNutritionProvider>
  );
};

export default React.memo(ScenarioContainer);
